import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Mic, MicOff, Square, Play, Pause, Camera, CheckCircle2,
  Loader2, Sparkles, X, Shield, FileText, Brain,
  AlertTriangle, Pill, Plus, Heart, Zap, ListChecks, FilePlus, TestTube,
} from 'lucide-react';
import { CLINICAL_SUGGESTIONS } from './doctorModeData';
import { SpecialtySignalsStrip } from './SpecialtySignalsStrip';
import { SegmentList, EmptySegmentPlaceholder } from './DiarizedTranscript';

export const RecordingPanel = ({
  patient, activeApt,
  isRecording, isPaused, elapsed, transcript, recordingMode,
  stopSession, formatTime,
  setIsPaused, onCancelRecording,
  recordingTab, setRecordingTab,
  uploadedDocs, setUploadedDocs,
  clinicalChecklist, setClinicalChecklist,
  smartPrep, smartPrepLoading, smartPrepError, fetchSmartPrep,
  dictation,
  lensService,
}) => (
  <>
    {/* Left Pane (60%) — Read-only Patient Chart */}
    <div className="w-3/5 overflow-y-auto p-5 space-y-4" data-testid="split-pane-chart">
      {/* Compact Patient Header */}
      <div className="flex items-center gap-3" data-testid="split-patient-header">
        <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">
          {patient.firstName?.[0]}{patient.lastName?.[0]}
        </div>
        <div>
          <p className="text-sm font-bold text-slate-900">{patient.firstName} {patient.lastName}</p>
          <p className="text-xs text-slate-400">{patient.gender} &middot; {patient.age}y &middot; {patient.mrn}</p>
        </div>
        {patient.insurance && <Badge className="bg-slate-100 text-slate-500 text-[9px] border-0 ml-auto">{patient.insurance}</Badge>}
      </div>

      {/* Allergies */}
      {patient.allergies?.some(a => a.severity === 'Severe') && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50/50">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
          <p className="text-[11px] text-red-700 font-medium">
            Allergy Alert: {patient.allergies.filter(a => a.severity === 'Severe').map(a => `${a.allergen} (${a.reaction})`).join(', ')}
          </p>
        </div>
      )}

      {/* Smart Prep (compact) */}
      {smartPrepLoading && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/30 p-3 animate-pulse" data-testid="split-smart-prep-loading">
          <div className="flex items-center gap-2 mb-1">
            <Loader2 className="h-3 w-3 text-violet-500 animate-spin" />
            <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Loading Smart Prep...</p>
          </div>
          <div className="h-3 bg-violet-100 rounded w-3/4 mt-2" />
          <div className="h-3 bg-violet-100 rounded w-1/2 mt-1" />
        </div>
      )}
      {!smartPrepLoading && smartPrep && (() => {
        const prep = smartPrep;
        return (
          <div className="rounded-xl border border-violet-200 bg-violet-50/30 p-3" data-testid="split-smart-prep">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-3 w-3 text-violet-600" />
              <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Smart Prep</p>
              <Badge className="bg-violet-100 text-violet-600 text-[7px] border-0 ml-auto">{prep.visitNumber} visit</Badge>
            </div>
            <p className="text-[10px] text-slate-600 leading-relaxed line-clamp-2">{prep.summary}</p>
            {prep.keyMetrics && prep.keyMetrics.length > 0 && (
              <div className="flex gap-2 mt-1.5 flex-wrap">
                {prep.keyMetrics.slice(0, 3).map((m, i) => (
                  <span key={i} className={cn("text-[9px] font-semibold",
                    m.status === 'above' ? 'text-red-600' : m.status === 'borderline' ? 'text-amber-600' : 'text-emerald-600')}>
                    {m.label}: {m.value}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })()}
      {!smartPrepLoading && smartPrepError && (
        <div className="rounded-xl border border-slate-200 bg-slate-50/30 p-3" data-testid="split-smart-prep-error">
          <div className="flex items-center gap-2">
            <Zap className="h-3 w-3 text-slate-400" />
            <p className="text-[10px] text-slate-400">Smart Prep unavailable</p>
            <button onClick={() => fetchSmartPrep(true)} className="text-[9px] text-violet-600 underline ml-auto" data-testid="split-smart-prep-retry">Retry</button>
          </div>
        </div>
      )}

      {/* AI Brief */}
      {patient.aiBrief && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <div className="h-7 w-7 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
              <Brain className="h-3.5 w-3.5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">AI Patient Brief</p>
              <p className="text-xs text-slate-600 leading-relaxed">{patient.aiBrief.summary}</p>
              {patient.aiBrief.attentionItems?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {patient.aiBrief.attentionItems.map((item, i) => (
                    <p key={i} className="text-[11px] text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3 shrink-0" />{item}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Glance — Meds & Vitals */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-2 mb-2">
            <Pill className="h-3.5 w-3.5 text-blue-500" />
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Medications</p>
            <Badge className="bg-slate-100 text-slate-500 text-[8px] border-0 ml-auto">{patient.medications?.length || 0}</Badge>
          </div>
          <div className="space-y-0.5">
            {patient.medications?.length > 0 ? patient.medications.map((m, i) => (
              <p key={i} className="text-[11px] text-slate-700"><span className="font-medium">{m.name}</span> <span className="text-slate-400">{m.freq}</span></p>
            )) : <p className="text-[10px] text-slate-400 italic">None</p>}
          </div>
        </div>
        {patient.vitals && (
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="h-3.5 w-3.5 text-red-400" />
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Vitals</p>
            </div>
            <div className="grid grid-cols-3 gap-1 text-center">
              {[
                { label: 'BP', value: patient.vitals.bp },
                { label: 'HR', value: patient.vitals.hr },
                { label: 'SpO2', value: patient.vitals.spo2 + '%' },
              ].map(v => (
                <div key={v.label}>
                  <p className="text-[9px] text-slate-400">{v.label}</p>
                  <p className="text-xs font-bold text-slate-800">{v.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Urgent Actionable Items */}
      {patient.actionableItems?.filter(i => i.priority === 'High').length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-3" data-testid="split-urgent-items">
          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-2">Urgent Clinical Items</p>
          <div className="space-y-1">
            {patient.actionableItems.filter(i => i.priority === 'High').map(item => (
              <p key={item.id} className="text-[11px] text-amber-800">&bull; {item.action}</p>
            ))}
          </div>
        </div>
      )}

      {/* Active Problems */}
      {patient.problems?.length > 0 && patient.problems[0].name !== 'No significant PMH' && (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Active Problems</p>
          <div className="space-y-1">
            {patient.problems.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]">
                <div className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
                <span className="text-slate-700">{p.name}</span>
                {p.icd && <Badge variant="outline" className="text-[8px] font-mono">{p.icd}</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    {/* Right Pane (40%) — Recording Widget */}
    <div className="w-2/5 flex flex-col border-l border-slate-200 bg-white" data-testid="split-pane-recording">
      {/* Consent Badge */}
      <div className="px-4 py-2.5 border-b border-emerald-100 bg-emerald-50/60 flex items-center gap-2" data-testid="consent-badge">
        <Shield className="h-3.5 w-3.5 text-emerald-600" />
        <p className="text-[10px] text-emerald-700 font-medium">Patient consent obtained for recording</p>
        <CheckCircle2 className="h-3 w-3 text-emerald-500 ml-auto" />
      </div>

      {/* Recording Status & Controls */}
      <div className="p-5 border-b border-slate-100 space-y-4">
        <div className="flex items-center gap-4">
          <div className={cn("h-14 w-14 rounded-full flex items-center justify-center transition-all shrink-0",
            isRecording && !isPaused ? "bg-red-500 shadow-lg shadow-red-200 animate-pulse" : isPaused ? "bg-amber-500" : "bg-slate-300")}>
            {isRecording && !isPaused ? <Mic className="h-6 w-6 text-white" /> : <Pause className="h-6 w-6 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-slate-800">{recordingMode === 'ambient' ? 'Ambient Listening' : 'Dictation'}</p>
              <Badge className={cn("text-[10px]", isRecording && !isPaused ? "bg-red-100 text-red-700 border-0" : "bg-slate-100 text-slate-600 border-0")}>
                {isRecording ? (isPaused ? 'Paused' : 'Live') : 'Stopped'}
              </Badge>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{patient.firstName} {patient.lastName}</p>
          </div>
          <p className="text-3xl font-mono font-bold text-slate-900 tabular-nums">{formatTime(elapsed)}</p>
        </div>

        {/* Real Dictation Status (chunk upload pipeline) */}
        {dictation && (
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100" data-testid="dictation-status">
            <div className="flex items-center gap-1.5">
              <div className={cn("h-2 w-2 rounded-full", dictation.recState === 'recording' ? 'bg-red-500 animate-pulse' : dictation.recState === 'paused' ? 'bg-amber-500' : 'bg-slate-300')} />
              <span className="text-[10px] font-semibold text-slate-500 uppercase">{dictation.recState === 'finalizing' ? 'Transcribing...' : 'Mic Live'}</span>
            </div>
            <div className="h-3 w-px bg-slate-200" />
            <span className="text-[10px] text-slate-400" data-testid="dictation-chunks">
              {dictation.chunksUploaded}/{dictation.totalChunks} chunks
            </span>
            {dictation.chunkQueue.length > 0 && (
              <Badge className="text-[8px] bg-blue-50 text-blue-600 border-0 animate-pulse">Uploading...</Badge>
            )}
          </div>
        )}

        {/* Primary Actions */}
        <div className="space-y-2">
          {isRecording && (
            <Button className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-sm font-bold gap-2.5 rounded-xl shadow-sm" onClick={stopSession} data-testid="stop-generate-btn">
              <Square className="h-4 w-4" /> Stop & Generate SOAP Note
            </Button>
          )}
          <div className="flex items-center gap-2">
            {isRecording && (
              <Button variant="outline" className="flex-1 h-9 text-xs gap-1.5" onClick={() => setIsPaused(!isPaused)} data-testid="split-pause-btn">
                {isPaused ? <><Play className="h-3.5 w-3.5" /> Resume</> : <><Pause className="h-3.5 w-3.5" /> Pause</>}
              </Button>
            )}
            <Button variant="ghost" className="h-9 text-xs text-slate-400 gap-1" onClick={onCancelRecording} data-testid="cancel-recording-btn">
              <X className="h-3.5 w-3.5" /> Cancel
            </Button>
          </div>
        </div>
      </div>

      {/* Right Panel Tabs + Lens Button */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
        <div className="flex gap-1">
          {[
            { key: 'clinical', label: 'Clinical', icon: ListChecks },
            { key: 'transcript', label: 'Transcript', icon: FileText },
            { key: 'documents', label: 'Documents', icon: FilePlus },
          ].map(t => (
            <button key={t.key} onClick={() => setRecordingTab(t.key)}
              className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all",
                recordingTab === t.key ? "bg-slate-900 text-white" : "text-slate-400 hover:bg-slate-100")}
              data-testid={`rec-tab-${t.key}`}>
              <t.icon className="h-3 w-3" />{t.label}
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => setRecordingTab('documents')} data-testid="lens-upload-btn">
          <Camera className="h-3 w-3" /> Lens
        </Button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4" data-testid="split-tab-content">

        {/* Clinical Tab — AI Real-time Suggestions */}
        {recordingTab === 'clinical' && (
          <div className="space-y-3" data-testid="clinical-tab">
            <SpecialtySignalsStrip />
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-3.5 w-3.5 text-violet-500" />
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">AI Clinical Suggestions</p>
              <Badge className="bg-violet-50 text-violet-600 text-[8px] border-0 ml-auto">Live</Badge>
            </div>
            <p className="text-[10px] text-slate-400 mb-2">Real-time orders & care gaps detected from conversation</p>
            {CLINICAL_SUGGESTIONS.slice(0, Math.min(3 + Math.floor(elapsed / 10), CLINICAL_SUGGESTIONS.length)).map(item => (
              <div key={item.id} className={cn("flex items-start gap-2 p-2.5 rounded-lg border transition-all",
                item.priority === 'high' ? 'border-red-100 bg-red-50/30' : item.priority === 'medium' ? 'border-amber-100 bg-amber-50/20' : 'border-slate-100 bg-slate-50/30')}
                data-testid={`suggestion-${item.id}`}>
                <input type="checkbox" checked={!!clinicalChecklist[item.id]} onChange={() => setClinicalChecklist(p => ({ ...p, [item.id]: !p[item.id] }))}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-violet-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Badge className={cn("text-[7px] border-0 uppercase",
                      item.type === 'medication' ? 'bg-blue-50 text-blue-600' : item.type === 'lab' ? 'bg-emerald-50 text-emerald-600' :
                      item.type === 'test' ? 'bg-violet-50 text-violet-600' : item.type === 'care_gap' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500')}>
                      {item.type.replace('_', ' ')}
                    </Badge>
                    {item.priority === 'high' && <Badge className="bg-red-100 text-red-700 text-[7px] border-0">Urgent</Badge>}
                  </div>
                  <p className="text-[11px] text-slate-700">{item.text}</p>
                </div>
                <button onClick={() => toast.success('Added to SOAP', { description: item.text.slice(0, 60) })}
                  className="text-[9px] font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 rounded transition-colors shrink-0"
                  data-testid={`add-suggestion-${item.id}`}>+ SOAP</button>
              </div>
            ))}
          </div>
        )}

        {/* Transcript Tab */}
        {recordingTab === 'transcript' && (
          <div data-testid="split-transcript">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Live Transcript</p>
              {/* Diarize toggle — locks once a session is active so users
                  don't flip the preference mid-capture (server-side cannot
                  retroactively re-run diarization). */}
              {dictation && (
                <label
                  className={cn(
                    'flex items-center gap-1.5 text-[10px] font-medium text-slate-500',
                    !!dictation.sessionId && 'opacity-60 cursor-not-allowed'
                  )}
                  data-testid="diarize-toggle-label"
                >
                  <input
                    type="checkbox"
                    checked={!!dictation.diarize}
                    onChange={(e) => dictation.setDiarize?.(e.target.checked)}
                    disabled={!!dictation.sessionId}
                    className="h-3 w-3 rounded border-slate-300 text-violet-600"
                    data-testid="diarize-toggle"
                  />
                  Diarize speakers
                </label>
              )}
            </div>
            {Array.isArray(dictation?.segments) && dictation.segments.length > 0 ? (
              <SegmentList segments={dictation.segments} speakers={dictation.speakers} />
            ) : transcript.length > 0 ? (
              <div className="space-y-3">
                {transcript.map((line, i) => (
                  <div key={i} className="flex gap-2 text-xs">
                    <span className="text-slate-400 font-mono w-8 shrink-0">{line.time}</span>
                    <div>
                      <span className={cn("font-semibold text-[11px]", line.speaker === 'Patient' ? "text-blue-600" : "text-violet-600")}>{line.speaker}</span>
                      <p className="text-slate-600 mt-0.5">{line.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptySegmentPlaceholder isRecording={isRecording} />
            )}
          </div>
        )}

        {/* Documents Tab — Lens Upload */}
        {recordingTab === 'documents' && (
          <div className="space-y-3" data-testid="documents-tab">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Upload via Lumina Lens</p>
            {/* Upload Zone */}
            <label htmlFor="rec-lens-upload" className="block border-2 border-dashed border-slate-200 hover:border-amber-300 rounded-xl p-6 text-center cursor-pointer hover:bg-amber-50/20 transition-all">
              <Camera className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <p className="text-xs font-semibold text-slate-700">Drop files or click to upload</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Lab reports, referrals, prior notes, images</p>
              <input id="rec-lens-upload" type="file" className="hidden" accept="image/*,.pdf" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const docId = `DOC-${Date.now()}`;
                const placeholderDoc = { id: docId, name: file.name, type: file.type.includes('pdf') ? 'PDF' : 'Image', addedToContext: false, status: 'processing', analysis: null };
                setUploadedDocs(prev => [placeholderDoc, ...prev]);
                try {
                  const job = await lensService.extractAndWait(file, {
                    patientId: patient?.id || '',
                    onProgress: (j) => {
                      setUploadedDocs(prev => prev.map(d => d.id === docId ? { ...d, status: j.status } : d));
                    },
                  });
                  if (job.status === 'completed' && job.extracted_fields) {
                    setUploadedDocs(prev => prev.map(d => d.id === docId ? {
                      ...d, status: 'completed', job_id: job.job_id,
                      analysis: { type: job.extracted_fields.document_type || 'Document', summary: job.extracted_fields.summary || '', sections: job.extracted_fields.sections || [] },
                    } : d));
                    toast.success('Document analyzed', { description: `${file.name} processed by Lens AI` });
                  } else {
                    setUploadedDocs(prev => prev.map(d => d.id === docId ? { ...d, status: 'failed', error: job.errors?.[0] || 'Extraction failed' } : d));
                    toast.error('Lens extraction failed', { description: job.errors?.[0] || 'Try again' });
                  }
                } catch (err) {
                  setUploadedDocs(prev => prev.map(d => d.id === docId ? { ...d, status: 'failed', error: err.message } : d));
                  toast.error('Lens upload failed', { description: err.message });
                }
              }} data-testid="rec-lens-file-input" />
            </label>

            {/* Uploaded Documents */}
            {uploadedDocs.map(doc => (
              <div key={doc.id} className={cn("rounded-xl border p-3 space-y-2",
                doc.status === 'failed' ? 'border-red-200 bg-red-50/30' :
                doc.status === 'processing' ? 'border-blue-200 bg-blue-50/30' :
                doc.addedToContext ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'
              )} data-testid={`uploaded-doc-${doc.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {doc.status === 'processing' ? <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" /> : <FileText className="h-3.5 w-3.5 text-amber-500" />}
                    <p className="text-xs font-semibold text-slate-700">{doc.name}</p>
                    <Badge className="text-[8px] bg-slate-100 text-slate-500 border-0">{doc.type}</Badge>
                    {doc.status === 'processing' && <Badge className="text-[8px] bg-blue-100 text-blue-700">Processing...</Badge>}
                    {doc.status === 'failed' && <Badge className="text-[8px] bg-red-100 text-red-700">Failed</Badge>}
                  </div>
                  {doc.status === 'failed' ? (
                    <Button size="sm" variant="outline" className="h-6 text-[9px] gap-1 border-red-200 text-red-700"
                      onClick={async () => {
                        if (!doc.job_id) return;
                        setUploadedDocs(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'processing' } : d));
                        try {
                          let job = await lensService.retryJob(doc.job_id);
                          if (job.status === 'processing') {
                            for (let i = 0; i < 30; i++) {
                              await new Promise(r => setTimeout(r, 2000));
                              job = await lensService.pollJob(job.job_id);
                              if (job.status === 'completed' || job.status === 'failed') break;
                            }
                          }
                          if (job.status === 'completed' && job.extracted_fields) {
                            setUploadedDocs(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'completed', analysis: { type: job.extracted_fields.document_type || 'Document', summary: job.extracted_fields.summary || '', sections: job.extracted_fields.sections || [] } } : d));
                            toast.success('Retry succeeded');
                          } else {
                            setUploadedDocs(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'failed', error: job.errors?.[0] } : d));
                          }
                        } catch (err) {
                          setUploadedDocs(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'failed', error: err.message } : d));
                        }
                      }}
                      data-testid={`retry-doc-${doc.id}`}>
                      <AlertTriangle className="h-2.5 w-2.5" /> Retry
                    </Button>
                  ) : doc.status !== 'processing' ? (
                    <Button size="sm" variant={doc.addedToContext ? "outline" : "default"}
                      className={cn("h-6 text-[9px] gap-1", doc.addedToContext ? "border-emerald-300 text-emerald-700" : "bg-slate-900 text-white")}
                      onClick={() => { setUploadedDocs(prev => prev.map(d => d.id === doc.id ? { ...d, addedToContext: !d.addedToContext } : d)); toast.success(doc.addedToContext ? 'Removed from context' : 'Added to SOAP context'); }}
                      data-testid={`add-context-${doc.id}`}>
                      {doc.addedToContext ? <><CheckCircle2 className="h-2.5 w-2.5" /> In context</> : <><Plus className="h-2.5 w-2.5" /> Add to context</>}
                    </Button>
                  ) : null}
                </div>
                {doc.status === 'failed' && doc.error && (
                  <p className="text-[10px] text-red-600">{doc.error}</p>
                )}
                {doc.analysis && (
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-[9px] font-bold text-slate-400 uppercase mb-1">Lens Analysis — {doc.analysis.type}</p>
                    <p className="text-[10px] text-slate-600">{doc.analysis.summary}</p>
                    {doc.analysis.sections?.map((s, si) => (
                      <div key={si} className="mt-1.5">
                        {s.items.map((item, ii) => (
                          <p key={ii} className="text-[10px] text-slate-600"><span className="font-medium">{item.key}:</span> {item.val}</p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {uploadedDocs.length === 0 && (
              <p className="text-[10px] text-slate-400 text-center py-4">No documents uploaded yet. Use Lens to add clinical documents mid-session.</p>
            )}
          </div>
        )}
      </div>
    </div>
  </>
);
